import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import BuildingCustomButton from "./BuildingCustomButton";
import {translate} from "../../types/EnumTranslations";
import {
    analyzeComponents,
    getHazardWarnings, getRiskWarnings
} from "../../Helpers/utils";
import ClickableImage from "../../Components/ClickableImage";
import {MapContainer, Marker, TileLayer} from "react-leaflet";
import ButtonWithIcon from "./ButtonWithIcon";
import button_contacts from "../../Images/button-icons/button_contacts.png"
import button_files from "../../Images/button-icons/button_files.png"
import button_images from "../../Images/button-icons/button_images.png"
import up_right_arrow from "../../Images/button-icons/up_right_arrow.png"
import button_stamp from "../../Images/button-icons/button_stamp.png"
import {Button} from "react-bootstrap";
import compass from "../../Images/compass.png"
import {BreadcrumbComponent} from "../../Components/BreadCrumb";
import noReception from "../../Images/no_reception.png"
import WarningViewComponent, {WarningColor} from "../../Components/WarningViewComponent";
import "./BuildingPage.css"
import {BaseType} from "../../types/Enums";
import {BaseComponent} from "../../types/protocols";
import {BaseComponentImpl} from "../../types/implementations";
import {useBuilding} from "../../Helpers/BuildingContext";
import {placeholderForBuildingType} from "../../Helpers/PlaceholderUtils";

const BuildingPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isPoorReceptionExpanded, setIsPoorReceptionExpanded] = useState(false);
    const { currentBuilding, error, loading, fetchBuilding } = useBuilding();

    useEffect(() => {
        const loadBuildingData = async () => {
            const pathSegments = location.pathname.split('/');
            const buildingKey = pathSegments[pathSegments.length - 1];

            await fetchBuilding(buildingKey);
        };

        loadBuildingData().then();
    }, [location.pathname, fetchBuilding]);

    if (error) {
        return <p>{error}</p>
    }

    if (loading) return <></>

    if (!currentBuilding) {
        return <div>Building not found</div>;
    }

    const { model, summary } = currentBuilding;

    const riskConstructions = getRiskWarnings(Object.values(model.structure?.components ?? []))
    const hazards = getHazardWarnings(Object.values(model.structure?.components ?? []))

    const filterComponentsByType = (components: { [key: string]: BaseComponent } | undefined, type: BaseType) =>
        Object.values(components || {}).filter(component => component.type === type);

    const roofComponents = model.structure?.components ? filterComponentsByType(model.structure.components, BaseType.Roof) : [];
    const facadeComponents = model.structure?.components ? filterComponentsByType(model.structure.components, BaseType.Facade) : [];
    const foundationComponents = model.structure?.components ? filterComponentsByType(model.structure.components, BaseType.Foundation) : [];
    const drainageComponents = model.structure?.components ? filterComponentsByType(model.structure.components, BaseType.Drainage) : [];
    const chimneyComponents = model.structure?.components ? filterComponentsByType(model.structure.components, BaseType.Chimney) : [];
    const outdoorComponents = model.structure?.components ? filterComponentsByType(model.structure.components, BaseType.Outdoor) : [];

    return (
        <div style={{marginBottom: '20px'}}>
            <BreadcrumbComponent
                items={[
                    {text: summary.name ?? 'Byggnad'},
                ]}
            />

            <div style={{ padding: '20px', backgroundColor: '#f4f4f4', width: '100%', height: '100%', maxWidth: '1100px' }}>
                <div style={{
                    position: 'relative',
                    width: '100%',
                    height: '240px',
                    borderTopLeftRadius: '10px',
                    borderTopRightRadius: '10px',
                    overflow: 'hidden'
                }} >
                    <img
                        src={summary.coverPhoto?.fullSizeUrl || placeholderForBuildingType(currentBuilding.summary.buildingType)}
                        alt="Background"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                        }}
                    />
                    <div style={{
                        position: 'absolute',
                        top: '15px',
                        right: '15px',
                        width: '210px',
                        height: '210px',
                        borderRadius: '10px',
                        zIndex: 2,
                        overflow: 'hidden',
                        boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)',
                    }} >
                        {summary.coordinates?.latitude && summary.coordinates?.longitude && (
                            <MapContainer
                                center={[summary.coordinates.latitude, summary.coordinates.longitude]}
                                zoom={13}
                                style={{ height: '100%', width: '100%' }}
                            >
                                <TileLayer
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                />
                                <Marker position={[summary.coordinates.latitude, summary.coordinates.longitude]} />
                            </MapContainer>
                        )}
                    </div>
                </div>

                <div
                    style={{
                        backgroundColor: 'white',
                        borderBottomLeftRadius: '10px',
                        borderBottomRightRadius: '10px',
                        padding: '20px',
                        paddingBottom: '30px',
                        marginBottom: '40px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        fontSize: '10pt',
                    }}
                >

                    <h3>{summary.name}</h3>
                    <br/>

                    <div style={{ display: 'flex', gap: '40px' }}>

                        {/* First Column: Buttons */}
                        <div style={{ flex: 1 }}>
                            <div>
                                <ButtonWithIcon path={'/blueprints'} imagePath={button_files}>
                                    Ritningar & dokument
                                    ({model.documents ?
                                    <>{Object.entries(model.documents).length}</>
                                    :
                                    <>0</>})
                                </ButtonWithIcon>

                                <ButtonWithIcon path={`/allPhotos`} imagePath={button_images}>
                                    Samtliga bilder
                                </ButtonWithIcon>

                                <ButtonWithIcon path={`/editContacts`} imagePath={button_contacts}>
                                    Kontakter
                                    ({model.contacts ?
                                    <>{Object.entries(model.contacts).length}</>
                                    :
                                    <>0</>})
                                </ButtonWithIcon>

                                <ButtonWithIcon path={`/permits`} imagePath={button_stamp}>
                                    Strandskydd & bygglov
                                    ({model.contacts ?
                                    <>{Object.entries(model.contacts).length}</>
                                    :
                                    <>0</>})
                                </ButtonWithIcon>

                            </div>
                        </div>

                        {/* Second Column: Address */}
                        <div style={{ flex: 1 }}>
                            <b>Adress</b>
                            <br/>
                            {summary.buildingInfo?.street ?? "[Gatuadress]"}
                            <br/>
                            {`${summary.buildingInfo?.postalCode ?? "[Postnummer]"} ${summary.buildingInfo?.city ?? "[Postort]"}`}
                            {summary.buildingInfo?.municipality &&
                                <>
                                    <br/><br/>
                                    <b>Kommun</b>
                                    <br/>
                                    {summary.buildingInfo?.municipality}
                                    <br/>

                                </>
                            }
                            {
                                summary?.coordinates?.latitude && summary?.coordinates?.longitude && (
                                    <>
                                        <br/>
                                        <b>Koordinater</b>
                                        <br/>
                                        <span style={{fontSize: '9pt'}}> {`${summary.coordinates.latitude}, ${summary.coordinates.longitude}`}</span>
                                        <br/>

                                        <Button
                                            variant="secondary"
                                            style={{
                                                fontSize: '9pt',
                                                borderRadius: '12px',
                                                padding: '2px 16px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                marginTop: '4px',
                                            }}
                                            onClick={() => window.open(
                                                `https://maps.google.com/?q=${summary.coordinates?.latitude},${summary.coordinates?.longitude}`,
                                                '_blank',
                                                'noopener,noreferrer')}
                                        >
                                            Öppna i Google Maps
                                            <img src={up_right_arrow}
                                                 alt={"Up Right Arrow"}
                                                 style={{
                                                     width: '14px',
                                                     height: '14px',
                                                     marginLeft: '3px',
                                                     filter: 'brightness(0) invert(1)',
                                                 }}

                                            />
                                        </Button>
                                    </>
                                )
                            }



                        </div>

                        {/* Third Column: Contact Info */}
                        <div style={{ flex: 1 }}>
                            <p>
                                <b>Nyckelinstruktioner</b>
                                <br/>
                                {summary.buildingInfo?.keyInstructions ?? "Ej angivet"}
                            </p>
                            <p>
                                <b>Övrigt</b>
                                <br/>
                                {summary.buildingInfo?.other ?? "Ej angivet"}
                            </p>
                            {
                                summary.buildingInfo?.hasPoorReception &&
                                <div>
                                    <div
                                        onClick={() => setIsPoorReceptionExpanded(!isPoorReceptionExpanded)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',}}>
                                        <img
                                            style={{width: '22px', height: '22px', marginRight: '10px'}}
                                            src={noReception}
                                            alt="No reception icon"
                                        />
                                        <span style={{color: '#DC143C', fontWeight: 'bold', fontSize: '9pt'}}>
                                            Ingen mottagning
                                        </span>
                                        <button
                                            style={{
                                                marginLeft: '5px',
                                                background: 'none',
                                                border: 'none',
                                                padding: '5px',
                                                color: '#DC143C'
                                            }}
                                        >
                                            {isPoorReceptionExpanded ? '▼' : '▲'}
                                        </button>
                                    </div>
                                    {isPoorReceptionExpanded && (
                                        <p style={{ marginTop: '10px' }}>
                                            Det finns ingen (eller väldigt bristfällig) mobiltäckning vid byggnaden.
                                            Viktigt att använda off grid-mode vid användning av Molnfastigheters App vid denna byggnad.
                                        </p>
                                    )}
                                </div>
                            }
                        </div>

                        <div style={{ flex: 1}}>
                            {
                                model.mapImage &&
                                <div style={{
                                    width: '150px',
                                    height: '150px',
                                    position: 'relative'
                                }}>
                                    <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%'
                                    }}>
                                        <ClickableImage
                                            buildingKey={model.key}
                                            photo={model.mapImage}
                                            size={150}
                                        />
                                    </div>
                                    <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        zIndex: 1,
                                        pointerEvents: 'none'
                                    }}>
                                        <img
                                            src={compass}
                                            alt={"Compass"}
                                            style={{
                                                padding: '8px',
                                                width: '100%',
                                                height: '100%'
                                            }}
                                        />
                                    </div>
                                </div>
                            }
                        </div>
                    </div>

                    <div style={{ display: 'flex', columnGap: '40px', rowGap: '25px', flexWrap: 'wrap', marginTop: '30px' }}>

                        {/* MILJÖ / HÄLSOFARA */}
                        {
                            hazards.length !== 0 &&
                            <WarningViewComponent color={WarningColor.WarningRed} title={"Miljö/hälsofara"} hazards={hazards} />
                        }

                        {
                            riskConstructions.length !== 0 &&
                            <WarningViewComponent title={"Riskkonstruktioner"} color={WarningColor.WarningOrange} hazards={riskConstructions} />
                        }

                    </div>

                </div>

                <div
                    style={{
                        backgroundColor: 'white',
                        borderRadius: '10px',
                        padding: '20px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    }}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap' }}>

                        <div style={{ flex: '1', minWidth: '200px' }}>
                            <h3 style={{ paddingBottom: '10px' }}>{"Klimatskal"}</h3>
                            <ul style={{ listStyleType: 'none', padding: 0 }}>
                                {roofComponents && <BuildingCustomButton title={translate(BaseType.Roof)} metadata={analyzeComponents(Object.values(roofComponents))} imagePath={'roof.png'} onClick={()=> {
                                    navigate(`${location.pathname}/roof`)
                                }}/>
                                }

                                {facadeComponents && <BuildingCustomButton title={translate(BaseType.Facade)} metadata={analyzeComponents(facadeComponents)} imagePath={'facade.png'} onClick={()=> {
                                    navigate(`${location.pathname}/facade`)
                                }}/>
                                }

                                {foundationComponents && <BuildingCustomButton title={translate(BaseType.Foundation)} metadata={analyzeComponents(foundationComponents)} imagePath={"foundation.png"} onClick={()=> {
                                    navigate(`${location.pathname}/foundation`)
                                }}/>
                                }

                                {drainageComponents && <BuildingCustomButton title={translate(BaseType.Drainage)} metadata={analyzeComponents(drainageComponents)} imagePath={'drainage.png'} onClick={()=> {
                                    navigate(`${location.pathname}/drainage`)
                                }}/>
                                }

                                {chimneyComponents && <BuildingCustomButton title={translate(BaseType.Chimney)} metadata={analyzeComponents(chimneyComponents)} imagePath={'chimney.png'} onClick={()=> {
                                    navigate(`${location.pathname}/chimney`)
                                }}/>
                                }
                            </ul>
                        </div>

                        <div style={{ flex: '1', minWidth: '200px' }}>
                            <h3 style={{ paddingBottom: '10px' }}>Våningsplan</h3>
                            <ul style={{ listStyleType: 'none', padding: 0 }}>
                                {model?.structure?.levels &&
                                    Object.entries(model.structure.levels)
                                        .sort(([, levelA], [, levelB]) => levelA.number - levelB.number)
                                        .map(([key, level]) => {
                                            const roomsForLevel = model.structure?.rooms
                                                ? Object.values(model.structure.rooms).filter(room => room.levelKey === level.key)
                                                : [];

                                            /*const totalArea = roomsForLevel.reduce((sum, room) => {
                                                return sum + (typeof room.area === 'number' ? room.area : 0);
                                            }, 0);*/

                                            const metadata = [
                                                `Antal rum: ${roomsForLevel.length}`,
                                                //...(totalArea > 0 ? [`Total area: ${totalArea} kvm`] : [])
                                            ];

                                            let imageString = "mellanvaning.png";
                                            if (level.number < 1) {
                                                imageString = "nedervaning.png";
                                            } else if (level.number > 1) {
                                                imageString = "overvaning.png";
                                            }

                                            return (
                                                <BuildingCustomButton
                                                    key={key}
                                                    title={`Våning ${level.number}` || translate(level.type) || "Odefinierat rum"}
                                                    metadata={metadata}
                                                    imagePath={imageString}
                                                    onClick={() => navigate(`${location.pathname}/level/${key}`)}
                                                />
                                            );
                                        })
                                }
                            </ul>
                        </div>

                        <div style={{ flex: '1', minWidth: '200px' }}>
                            <h3 style={{ paddingBottom: '10px' }}>{"Utemiljö"}</h3>
                            <ul style={{ listStyleType: 'none', padding: 0 }}>
                                {outdoorComponents &&
                                    Object.entries(outdoorComponents).map(([key, item]) => {
                                        const outdoor = new BaseComponentImpl(item)
                                        return (
                                            <BuildingCustomButton
                                                title={outdoor.displayName()|| translate(item.specificType) || 'Odefinierat'}
                                                metadata={[translate(item.specificType)]}
                                                onClick={()=> {
                                                    navigate(`${location.pathname}/component/${item.key}`)}
                                                }
                                                imagePath={`BuildTypes/${item.specificType}.png`}
                                            />
                                        );
                                    })}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>


    );
};

export default BuildingPage;
